import { isBdApiEnabled } from 'config/bdApiConfig';
import {
  cacheGet,
  cacheSet,
  commonSaveList,
  commonSelect,
} from 'services/commonQuery';

/**
 * 기능개선관리대장 — HiReact 제품 백로그 (테넌트 스코프 없음)
 * API: CmFeature.*  /  아니면 localStorage
 */

export const SAVE_QUERY_FEATURE = [
  'CmFeature.insertFeature',
  'CmFeature.updateFeature',
  'CmFeature.deleteFeature',
];

export const FEATURE_STORAGE_KEY = 'erp.master.featureImprovement';
const CACHE_KEY = '_';
const featureCache = new Map();

/** 메뉴 모듈 progId와 1:1. 공통코드로 관리하지 않는다. */
export const FEATURE_MODULE_OPTIONS = [
  { value: 'HR', label: '인사관리' },
  { value: 'ADM', label: '기준정보' },
  { value: 'BUD', label: '예산관리' },
  { value: 'EXP', label: '지출관리' },
  { value: 'INC', label: '수입관리' },
  { value: 'CTR', label: '계약관리' },
  { value: 'TX', label: '세무관리' },
  { value: 'SH', label: '샵관리' },
  { value: 'MIG', label: '자료이관' },
  { value: 'AR', label: 'AI 문서 아카이브' },
];

const MODULE_SET = new Set(FEATURE_MODULE_OPTIONS.map((o) => o.value));

export function createFeatureUid() {
  if (typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function') {
    return `FT-${crypto.randomUUID()}`;
  }
  return `FT-${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 9)}`;
}

export function emptyFeature(partial = {}) {
  return {
    featureUid: '',
    seqNo: '',
    moduleCd: 'BUD',
    itemNm: '',
    content: '',
    devDate: '',
    appliedDate: '',
    deployDate: '',
    remark1: '',
    remark2: '',
    version: 0,
    mod: '',
    ...partial,
  };
}

/** 일자 유무로만 계산. DB에 저장하지 않는다. */
export function progressStatusOf(row = {}) {
  if (row.deployDate) return '배포완료';
  if (row.appliedDate) return '반영완료';
  if (row.devDate) return '구현중';
  return '요청중';
}

export function enrichFeatureRow(row) {
  const r = row || {};
  return {
    ...r,
    seqNo: r.seqNo == null || r.seqNo === '' ? '' : Number(r.seqNo) || r.seqNo,
    progressStatus: progressStatusOf(r),
  };
}

function readLocal() {
  try {
    const raw = localStorage.getItem(FEATURE_STORAGE_KEY);
    if (!raw) return [];
    const parsed = JSON.parse(raw);
    return Array.isArray(parsed) ? parsed : [];
  } catch {
    return [];
  }
}

function writeLocal(rows) {
  localStorage.setItem(FEATURE_STORAGE_KEY, JSON.stringify(rows || []));
}

export function getCachedFeatures() {
  return cacheGet(featureCache, CACHE_KEY) || [];
}

/**
 * @param {{ moduleCd?: string, dateKind?: string, dateFrom?: string, dateTo?: string }} [filter]
 */
export async function loadFeatureList(filter = {}) {
  const moduleCd = String(filter.moduleCd || '').trim();
  const dateKind = String(filter.dateKind || 'devDate').trim() || 'devDate';
  const dateFrom = String(filter.dateFrom || '').trim();
  const dateTo = String(filter.dateTo || '').trim();

  if (isBdApiEnabled()) {
    const rows = await commonSelect('CmFeature.selectFeatureList', {
      moduleCd,
      dateKind,
      dateFrom,
      dateTo,
    });
    const mapped = (rows || []).map((r) => enrichFeatureRow({ ...r, mod: '' }));
    cacheSet(featureCache, CACHE_KEY, mapped);
    return mapped;
  }

  let list = (readLocal() || []).map((r) => enrichFeatureRow({ ...r, mod: '' }));
  if (moduleCd) list = list.filter((r) => r.moduleCd === moduleCd);
  const dateField =
    dateKind === 'appliedDate' ? 'appliedDate' : dateKind === 'deployDate' ? 'deployDate' : 'devDate';
  if (dateFrom) list = list.filter((r) => r[dateField] && String(r[dateField]) >= dateFrom);
  if (dateTo) list = list.filter((r) => r[dateField] && String(r[dateField]) <= dateTo);
  list.sort((a, b) => (Number(a.seqNo) || 0) - (Number(b.seqNo) || 0));
  cacheSet(featureCache, CACHE_KEY, list);
  return list;
}

/**
 * 그리드에서 넘어온 A/U/D 행만 저장한다.
 * 조회 필터로 빠진 행은 삭제 대상으로 보지 않는다.
 */
export async function saveFeatureList(dirtyRows = []) {
  const dirty = (dirtyRows || [])
    .filter((r) => r && (r.mod === 'A' || r.mod === 'U' || r.mod === 'D'))
    .map((r) => ({
      ...r,
      featureUid: r.featureUid || createFeatureUid(),
    }));

  if (isBdApiEnabled()) {
    if (dirty.length) {
      await commonSaveList(SAVE_QUERY_FEATURE, dirty);
    }
    const saved = (await commonSelect('CmFeature.selectFeatureList', {})).map((r) =>
      enrichFeatureRow({ ...r, mod: '' })
    );
    cacheSet(featureCache, CACHE_KEY, saved);
    return saved;
  }

  const prevAll = (getCachedFeatures().length ? getCachedFeatures() : readLocal()).map((r) =>
    enrichFeatureRow({ ...r, mod: '' })
  );
  const byUid = new Map(prevAll.filter((r) => r.featureUid).map((r) => [String(r.featureUid), r]));
  let maxSeq = prevAll.reduce((acc, r) => {
    const n = Number(r.seqNo) || 0;
    return n > acc ? n : acc;
  }, 0);
  dirty.forEach((r) => {
    const uid = String(r.featureUid);
    if (r.mod === 'D') {
      byUid.delete(uid);
      return;
    }
    const prev = byUid.get(uid);
    let seqNo = r.seqNo == null || r.seqNo === '' ? prev?.seqNo : r.seqNo;
    if (seqNo == null || seqNo === '') {
      maxSeq += 1;
      seqNo = maxSeq;
    }
    byUid.set(uid, enrichFeatureRow({ ...r, seqNo, mod: '' }));
  });
  const out = [...byUid.values()].sort((a, b) => (Number(a.seqNo) || 0) - (Number(b.seqNo) || 0));
  writeLocal(out);
  cacheSet(featureCache, CACHE_KEY, out);
  return out;
}

/**
 * @returns {string[]}
 */
export function validateFeatureRows(rows = []) {
  const errors = [];
  (rows || [])
    .filter((r) => r && r.mod !== 'D')
    .forEach((r, idx) => {
      const label = r.itemNm || `#${idx + 1}`;
      if (!MODULE_SET.has(String(r.moduleCd || '').trim())) {
        errors.push(`모듈 필수: ${label}`);
      }
      const itemNm = String(r.itemNm || '').trim();
      if (!itemNm) {
        errors.push(`항목 필수: ${label}`);
      } else if (itemNm.length > 200) {
        errors.push(`항목 200자 초과: ${label}`);
      }
      if (String(r.content || '').length > 2000) {
        errors.push(`내용 2000자 초과: ${label}`);
      }
      if (String(r.remark1 || '').length > 500) {
        errors.push(`비고1 500자 초과: ${label}`);
      }
      if (String(r.remark2 || '').length > 500) {
        errors.push(`비고2 500자 초과: ${label}`);
      }
    });
  return errors;
}
